﻿import React, { useCallback } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Fade, MenuItem, MenuList, Paper, Popper } from "@mui/material";
import { Link, useLocation } from "react-router";
import { useNavigationLinkStyles } from "./NavigationLink.styles";
import { DotLegalTooltip, ToDto, UrlToPathObject } from "@dotlegal/dotlegal-ui-components";
import PrettyScrollbar from "../../common/PrettyScrollbar";
import { SubMenuItem } from "./NavigationLink.types";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useUrlProvider } from "../../useUrlProvider";
import OverflowTooltip from "@common/components/OverflowTooltip";
import { getActiveSubMenuItemIndex, isMenuItemActive, MenuItemPaths } from "./NavigationLink.utils";

export interface INavigationLinkProps {
    text: string;
    linkTo: ToDto | string;
    subMenuItems?: Array<SubMenuItem>;
    icon: any;
    isExpanded: boolean;
    isInGroup?: boolean;
    openNewTab?: boolean;
    externalLink?: boolean;
    tooltip?: string;
    extraPaths: Array<string>;
    onClick: () => void;
    htmlId: string;
    onSubMenuExpanded?: (menuItem: string | false) => void;
    subMenuExpanded?: string | false;
    isExpandedSmallScreen?: boolean;
}

function hasSubMenuItems(props: INavigationLinkProps) {
    return props.subMenuItems && props.subMenuItems.length > 0;
}

function toMenuItemPaths(link: ToDto | string, extraPaths: Array<string> | undefined): MenuItemPaths {
    return { linkPathname: UrlToPathObject(link).pathname ?? "", extraPaths: extraPaths ?? [] };
}

function NavigationLink(props: INavigationLinkProps) {
    const [hover, setHover] = React.useState<{ anchorElement: HTMLElement; pathname: string } | null>(null);

    const location = useLocation();
    // Navigating folds and unfolds sections under a still cursor, and the browser does not reliably send mouseleave
    // then, so a hover only counts on the page it started on
    const anchorElement = hover?.pathname === location.pathname ? hover.anchorElement : null;
    const urlProvider = useUrlProvider();
    const userContext = useUserContext();

    const isRouteLink = useCallback(() => {
        if (userContext.permissions.processingActivityPermissions.read) return urlProvider.getProcessingActivitiesOverviewUrl();
        else if (userContext.permissions.canAccessRecord301 || userContext.permissions.canAccessRecord302) return urlProvider.getRecordUrl();
        else if (userContext.permissions.systemPermissions.read) return urlProvider.getSystemsUrl();
        else if (userContext.permissions.riskPermissions.read && userContext.showLegacyRiskAssessments) return urlProvider.getRiskAssessmentsUrl();
        else if (userContext.permissions.annualWheelPermission.read) return urlProvider.getAnnualWheelUrl();
        else if (userContext.permissions.taskPermissions.read) return urlProvider.getTaskManagementUrl();
        else if (userContext.permissions.companyPermissions.read) return urlProvider.getExternalLegalEntities();

        return "";
    }, [urlProvider, userContext.permissions, userContext.showLegacyRiskAssessments]);

    const rootRoutePathname = isRouteLink();
    const isMainMenuItemActive = isMenuItemActive(location.pathname, rootRoutePathname, toMenuItemPaths(props.linkTo, props.extraPaths));
    const activeSubMenuItemIndex = getActiveSubMenuItemIndex(
        location.pathname,
        rootRoutePathname,
        props.subMenuItems?.map((s) => toMenuItemPaths(s.link, s.extraPaths)) ?? []
    );

    const styles = useNavigationLinkStyles(props.isExpanded, isMainMenuItemActive, props.isInGroup);

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
        setHover({ anchorElement: event.currentTarget, pathname: location.pathname });
    };

    function handlePopoverClose() {
        setHover(null);
    }

    // Touch devices cannot hover, so the small screen drawer keeps every sub menu inline; on big screens only the section you are in can unfold and every folded sub menu opens on hover
    const canUnfoldSubMenu = props.isExpanded && (props.isExpandedSmallScreen || isMainMenuItemActive);
    let accordionExpanded = props.subMenuExpanded === props.text && canUnfoldSubMenu;
    const showSubMenuOnHover = !accordionExpanded && !(props.isExpanded && props.isExpandedSmallScreen);

    function accordion(styles: any, props: INavigationLinkProps) {
        const accordionSummery = (
            <AccordionSummary
                sx={{
                    width: "100%",
                    height: "100%",
                    paddingLeft: "0px !important",
                    paddingRight: "0px !important",
                    borderRadius: "15px",
                    textAlign: isMainMenuItemActive ? "left" : "center",
                    color: isMainMenuItemActive ? "black" : "inherit",
                    "&:hover": { backgroundColor: "primary.light", color: "primary.main" },
                    "& .MuiAccordionSummary-content": {
                        margin: 0,
                        overflow: "hidden",
                        minHeight: 0,
                        height: "100%",
                        "&.Mui-expanded": {
                            margin: 0,
                        },
                    },
                }}
                expandIcon={props.isExpanded ? <ExpandMoreIcon /> : undefined}
            >
                {content}
            </AccordionSummary>
        );

        const accordionSummeryWrapperStyles = {
            ...styles.link,
            ...styles.menuItem,
            backgroundColor: isMainMenuItemActive && !accordionExpanded ? "primary.light" : "transparent",
            "& .MuiBox-root": {
                color: isMainMenuItemActive && !accordionExpanded ? "primary.main" : "black",
            },
            "&:hover": {
                backgroundColor: "primary.light",
                "& .MuiBox-root": {
                    color: "primary.main",
                },
            },
        };

        return (
            <Accordion
                expanded={accordionExpanded}
                onChange={(e, expanded) => {
                    if ((isMainMenuItemActive || props.isExpandedSmallScreen) && props.onSubMenuExpanded) {
                        props.onSubMenuExpanded(expanded ? props.text : false);
                    }
                }}
                sx={(theme) => ({
                    "& .MuiAccordionDetails-root": {
                        margin: 0,
                    },
                    "& .MuiAccordionSummary-root": {
                        textDecoration: "none",
                        fontWeight: theme.typography.fontWeightBold,
                        minHeight: "0 !important",
                        textTransform: "uppercase",
                        textOverflow: "ellipsis",
                        fontSize: theme.typography.pxToRem(14),
                        fontFamily: theme.typography.fontFamily,
                        "&:hover": {
                            backgroundColor: "transparent",
                        },
                    },
                })}
                className={"couldBeActive" + isMainMenuItemActive ? " active" : ""}
            >
                {/* A sub menu that opens on hover shows the tooltip on the popper heading instead, so it does not cover the popper */}
                <DotLegalTooltip text={showSubMenuOnHover ? undefined : props.tooltip} position="right" tooltipStyle="primary" useBoldText>
                    {/* The box is used as anchor element for the tooltip */}
                    <Box>
                        {isMainMenuItemActive || props.isExpandedSmallScreen ? (
                            <Box id={props.htmlId} sx={{ ...accordionSummeryWrapperStyles }}>
                                {accordionSummery}
                            </Box>
                        ) : (
                            <Box
                                id={props.htmlId}
                                component={Link}
                                sx={{ ...accordionSummeryWrapperStyles }}
                                target={props.openNewTab ? "_blank" : "_self"}
                                rel={props.openNewTab ? "noopener noreferrer" : ""}
                                onClick={() => {
                                    if (!isMainMenuItemActive && props.onSubMenuExpanded) {
                                        props.onSubMenuExpanded(props.text);
                                    }
                                    props.onClick();
                                    handlePopoverClose();
                                }}
                                to={props.linkTo}
                            >
                                {accordionSummery}
                            </Box>
                        )}
                    </Box>
                </DotLegalTooltip>

                <AccordionDetails>
                    {props.subMenuItems?.map((s, index) => {
                        let isActive = index === activeSubMenuItemIndex;
                        return (
                            <DotLegalTooltip key={s.htmlId} text={s.tooltip} position="right" tooltipStyle="primary" useBoldText>
                                <Box
                                    id={s.htmlId}
                                    component={Link}
                                    sx={[
                                        styles.link,
                                        styles.subMenuLink,
                                        { backgroundColor: isActive ? "primary.light" : "inherit", color: isActive ? "primary.main" : "inherit" },
                                    ]}
                                    to={s.link}
                                    onClick={() => {
                                        if (props.isExpandedSmallScreen) {
                                            props.onClick();
                                        }
                                    }}
                                >
                                    <OverflowTooltip text={s.name} sx={{ marginLeft: "45px" }} />
                                </Box>
                            </DotLegalTooltip>
                        );
                    })}
                </AccordionDetails>
            </Accordion>
        );
    }

    let content = (
        <React.Fragment>
            {!props.isExpanded && hasSubMenuItems(props) ? <Box sx={styles.menuIcon}>{props.icon}</Box> : <Box sx={styles.menuIcon}>{props.icon}</Box>}

            <Box component={"span"} sx={[styles.text, props.isExpanded ? {} : styles.isMinimized]}>
                {props.text}
            </Box>
        </React.Fragment>
    );

    if (hasSubMenuItems(props) && props.isExpanded) {
        content = accordion(styles, props);
    } else if (props.linkTo) {
        content = (
            <Box
                id={props.htmlId}
                component={Link}
                sx={{ ...styles.link, ...styles.menuItem }}
                target={props.openNewTab ? "_blank" : "_self"}
                rel={props.openNewTab ? "noopener noreferrer" : ""}
                onClick={() => {
                    props.onClick();
                    if (!isMainMenuItemActive && props.onSubMenuExpanded) {
                        props.onSubMenuExpanded(props.text);
                    }
                    handlePopoverClose();
                }}
                to={props.linkTo}
            >
                {content}
            </Box>
        );
    }

    if (!props.isExpanded && !hasSubMenuItems(props)) {
        content = (
            <DotLegalTooltip text={`${props.text} ${props.tooltip ? `\n\n ${props.tooltip}` : ""}`} position="right" tooltipStyle="primary" useBoldText>
                {content}
            </DotLegalTooltip>
        );
    } else if (props.tooltip && !hasSubMenuItems(props)) {
        content = (
            <DotLegalTooltip text={props.tooltip} position="right" tooltipStyle="primary" useBoldText>
                {content}
            </DotLegalTooltip>
        );
    }

    let open = (Boolean(anchorElement) && hasSubMenuItems(props) && showSubMenuOnHover) ?? false;

    return (
        <Box onMouseEnter={handlePopoverOpen} onMouseLeave={handlePopoverClose}>
            <Box component={"li"} sx={styles.wrapper}>
                {content}
            </Box>

            <Popper sx={styles.popOverSubMenu} open={open} anchorEl={anchorElement} placement={"right-start"} transition>
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={350}>
                        <Paper sx={{ padding: 1 }}>
                            <MenuList>
                                <DotLegalTooltip text={props.tooltip} position="right" tooltipStyle="primary" useBoldText>
                                    <Box component={"h4"} sx={(theme) => ({ fontSize: theme.typography.pxToRem(14), textTransform: "uppercase", height: 40 })}>
                                        {props.text}
                                    </Box>
                                </DotLegalTooltip>
                                <PrettyScrollbar maxHeight={350}>
                                    {props.subMenuItems?.map((s, index) => {
                                        let isActive = index === activeSubMenuItemIndex;
                                        const menuItem = (
                                            <DotLegalTooltip text={s.tooltip} position="right" tooltipStyle="primary" useBoldText>
                                                <MenuItem
                                                    onClick={() => {
                                                        handlePopoverClose();
                                                        if (!isMainMenuItemActive && props.onSubMenuExpanded) {
                                                            props.onSubMenuExpanded(props.text);
                                                        }
                                                    }}
                                                    sx={(theme) => ({
                                                        ...styles.collapsedSubMenuItem,
                                                        backgroundColor: isActive ? "primary.light" : "inherit",
                                                        color: isActive ? "primary.main" : theme.palette.grey["700"],
                                                        marginBottom: 0.5,
                                                    })}
                                                >
                                                    <OverflowTooltip text={s.name} />
                                                </MenuItem>
                                            </DotLegalTooltip>
                                        );
                                        return (
                                            <Box component={Link} key={s.link + s.htmlId} sx={styles.subMenuPopperLink} to={s.link} color={"inherit"}>
                                                <Box>{menuItem}</Box>
                                            </Box>
                                        );
                                    })}
                                </PrettyScrollbar>
                            </MenuList>
                        </Paper>
                    </Fade>
                )}
            </Popper>
        </Box>
    );
}

export default NavigationLink;
